"use client";

import { useEffect, useRef } from "react";

const VERT = `
attribute vec2 position;
varying vec2 v;
void main(){
  v = position;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const FRAG = `
precision mediump float;
varying vec2 v;
uniform float t;
uniform vec2 r;
uniform vec2 c;
uniform float ci;

uniform vec3 u_pal_base;
uniform vec3 u_pal_warm;
uniform vec3 u_pal_mid;
uniform vec3 u_pal_cool;
uniform vec3 u_pal_cursor;
uniform vec3 u_pal_rgScale;
uniform float u_brightness;

float h(vec2 p){
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float n(vec2 u){
  vec2 i = floor(u);
  vec2 f = fract(u);
  f = f*f*(3.0 - 2.0*f);
  float a = h(i);
  float b = h(i + vec2(1.0, 0.0));
  float c = h(i + vec2(0.0, 1.0));
  float d = h(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float m(vec2 u){
  return (n(u)*0.5 + n(u*2.0)*0.25) * 1.06667;
}

void main(){
  vec2 uv = (v * 0.5 + 0.5) * r / r.x;

  vec2 cn = c / r.x;
  vec2 toC = uv - cn;
  float dC = length(toC);
  float fall = exp(-dC * 9.0) * ci;

  vec2 sh = vec2(
    n(uv * 6.0 + vec2(t * 0.9,  0.0)),
    n(uv * 6.0 + vec2(0.0, t * 1.1))
  ) - 0.5;
  vec2 disp = sh * fall * 0.55;

  uv = uv * 5.0 + disp * 5.0;

  vec2 d1 = vec2( 0.18,  0.07) * t;
  vec2 d2 = vec2(-0.13,  0.21) * t;
  vec2 d3 = vec2( 0.09, -0.16) * t;

  float a = m(uv + d1);
  vec3 col = mix(u_pal_base, u_pal_warm, a * 2.5);

  float b = m(uv + a * 2.4 + d2);
  col = mix(col, u_pal_mid, b * 1.5);

  float dd = m(uv + b * 3.5 + d3);
  col = mix(col, u_pal_cool, dd);

  col += u_pal_cursor * fall;

  col *= u_pal_rgScale;
  gl_FragColor = vec4(col * u_brightness, 1.0);
}
`;

const PALETTE = {
  base: [0.02, 0.03, 0.09],
  warm: [0.09, 0.3, 0.9],
  mid: [0.22, 0.18, 0.62],
  cool: [0.1, 0.34, 0.52],
  cursor: [0.05, 0.1, 0.24],
  rgScale: [0.9, 0.95, 1.0],
  brightness: 2.2,
} as const;

const DPR_CAP = 1.5;
const FRAME_MS = 1000 / 60;

function compileProgram(gl: WebGLRenderingContext): WebGLProgram | null {
  const vertex = gl.createShader(gl.VERTEX_SHADER);
  const fragment = gl.createShader(gl.FRAGMENT_SHADER);
  const program = gl.createProgram();
  if (!vertex || !fragment || !program) return null;
  gl.shaderSource(vertex, VERT);
  gl.compileShader(vertex);
  gl.shaderSource(fragment, FRAG);
  gl.compileShader(fragment);
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  return program;
}

export function AuthShader({ className }: { className?: string }) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const canvas = document.createElement("canvas");
    canvas.style.position = "absolute";
    canvas.style.inset = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.display = "block";
    host.appendChild(canvas);

    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: false,
      powerPreference: "high-performance",
    });
    if (!gl) {
      canvas.remove();
      return;
    }

    const program = compileProgram(gl);
    if (!program) {
      canvas.remove();
      return;
    }
    gl.useProgram(program);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW,
    );
    const positionLocation = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    const uniform = (name: string) => gl.getUniformLocation(program, name);
    const timeLocation = uniform("t");
    const resolutionLocation = uniform("r");
    const cursorLocation = uniform("c");
    const cursorIntensityLocation = uniform("ci");

    gl.uniform3fv(uniform("u_pal_base"), PALETTE.base);
    gl.uniform3fv(uniform("u_pal_warm"), PALETTE.warm);
    gl.uniform3fv(uniform("u_pal_mid"), PALETTE.mid);
    gl.uniform3fv(uniform("u_pal_cool"), PALETTE.cool);
    gl.uniform3fv(uniform("u_pal_cursor"), PALETTE.cursor);
    gl.uniform3fv(uniform("u_pal_rgScale"), PALETTE.rgScale);
    gl.uniform1f(uniform("u_brightness"), PALETTE.brightness);

    const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);

    const render = () => {
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    let resizePending = 0;
    let lastW = 0;
    let lastH = 0;
    const resize = () => {
      const rect = host.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      if (w === lastW && h === lastH) return;
      lastW = w;
      lastH = h;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      render();
    };
    const queueResize = () => {
      if (resizePending) return;
      resizePending = requestAnimationFrame(() => {
        resizePending = 0;
        resize();
      });
    };
    resize();

    const ro = new ResizeObserver(queueResize);
    ro.observe(host);

    const target: [number, number] = [0, 0];
    const current: [number, number] = [0, 0];
    let targetCi = 0;
    let currentCi = 0;
    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        targetCi = 0;
        return;
      }
      const x = (e.clientX - rect.left) * (canvas.width / rect.width);
      const yTop = (e.clientY - rect.top) * (canvas.height / rect.height);
      target[0] = x;
      target[1] = canvas.height - yTop;
      targetCi = 1;
    };
    const onLeave = () => {
      targetCi = 0;
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerleave", onLeave, { passive: true });
    window.addEventListener("blur", onLeave);

    let raf = 0;
    const start = performance.now();
    let last = start;

    let visible = true;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      if (!visible) {
        cancelAnimationFrame(raf);
        raf = 0;
        return;
      }
      const now = performance.now();
      const dt = now - last;
      if (dt < FRAME_MS - 1) return;
      last = now - (dt % FRAME_MS);

      const k = 1 - (1 - 0.12) ** (dt / FRAME_MS);
      current[0] += (target[0] - current[0]) * k;
      current[1] += (target[1] - current[1]) * k;

      const ki = 1 - (1 - 0.06) ** (dt / FRAME_MS);
      currentCi += (targetCi - currentCi) * ki;

      gl.uniform1f(timeLocation, (now - start) * 0.001);
      gl.uniform2f(cursorLocation, current[0], current[1]);
      gl.uniform1f(cursorIntensityLocation, currentCi);
      render();
    };

    const io = new IntersectionObserver(
      (entries) => {
        visible = entries[0]?.isIntersecting ?? true;
        if (visible && !raf && !reduceMotion) {
          last = performance.now();
          loop();
        }
      },
      { threshold: 0 },
    );
    io.observe(host);

    if (reduceMotion) {
      gl.uniform1f(timeLocation, 0);
      render();
    } else {
      loop();
    }

    const onVis = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
        raf = 0;
      } else if (visible && !raf && !reduceMotion) {
        last = performance.now();
        loop();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(resizePending);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("blur", onLeave);
      document.removeEventListener("visibilitychange", onVis);
      gl.deleteProgram(program);
      gl.deleteBuffer(positionBuffer);
      canvas.remove();
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, []);

  return (
    <div
      ref={hostRef}
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    />
  );
}
